import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ContentHeader from '../components/ContentHeader'
import LoadingPlaceholder from '../components/LoadingPlaceholder'
import RecapDaySchedule from '../components/RecapDaySchedule'
import RecapPlaceholder from '../components/RecapPlaceholder'
import RecapTileGrid from '../components/RecapTileGrid'
import RecapWeekList from '../components/RecapWeekList'
import EmptyState from '../components/EmptyState'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import { listChats, archiveChat } from '../api/chat'
import { listConversations, archiveConversation } from '../api/crm'
import { isThisWeekUtc, isTodayUtc } from '../utils/timezone'
import '../style/views/DashboardView.css'

const filters = ['All', 'Today', 'This Week']
const DASHBOARD_FILTER_STORAGE_KEY = 'callNotesActiveFilter'
const isDashboardFilter = (value: string | null): value is (typeof filters)[number] =>
  filters.includes(value as string)

type DashboardItem = {
  id: string
  title?: string
  date_created?: string
  tags?: string[] | string | null
  type: 'CALL' | 'CHAT'
}

function DashboardView() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeFilter, setActiveFilter] = useState<(typeof filters)[number]>(() => {
    const filterParam = searchParams.get('filter')
    if (isDashboardFilter(filterParam)) return filterParam

    if (typeof window === 'undefined') return 'All'
    const stored = window.localStorage.getItem(DASHBOARD_FILTER_STORAGE_KEY)
    return isDashboardFilter(stored) ? stored : 'All'
  })
  const [loading, setLoading] = useState(false)
  const timeoutRef = useRef<number | null>(null)
  const [items, setItems] = useState<DashboardItem[]>([])
  const [dataLoading, setDataLoading] = useState(true)
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [archivingSelected, setArchivingSelected] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (isDashboardFilter(filterParam) && filterParam !== activeFilter) {
      setActiveFilter(filterParam)
    }
  }, [searchParams, activeFilter])

  const sortedItems = useMemo(() => {
    const parseDate = (value?: string) => {
      if (!value) return 0
      const parsed = Date.parse(value)
      return Number.isNaN(parsed) ? 0 : parsed
    }

    return items.toSorted((a, b) => parseDate(b.date_created) - parseDate(a.date_created))
  }, [items])

  const showChatsInNotes = localStorage.getItem('myscribbl_show_chats_in_notes') === 'true'

  const filteredItems = useMemo(() => {
    let result = sortedItems

    // 1. Apply active filter
    if (activeFilter === 'Today') result = result.filter((item) => isTodayUtc(item.date_created))
    else if (activeFilter === 'This Week') result = result.filter((item) => isThisWeekUtc(item.date_created))

    // 2. Check for chat visibility setting
    if (!showChatsInNotes) {
      result = result.filter(item => item.type !== 'CHAT')
    }


    return result
  }, [activeFilter, sortedItems, showChatsInNotes])

  useEffect(() => {
    setSelectedIds(new Set())
    setArchiveError(null)
  }, [activeFilter])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(DASHBOARD_FILTER_STORAGE_KEY, activeFilter)
  }, [activeFilter])

  const placeholderVariant = useMemo(() => {
    if (activeFilter === 'Today') return 'today'
    if (activeFilter === 'This Week') return 'week'
    return 'all'
  }, [activeFilter])

  useEffect(() => {
    const controller = new AbortController()
    const loadData = async () => {
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) {
        setDataLoading(false)
        return
      }
      if (!userId) setUserId(ensuredId)
      try {
        setDataLoading(true)

        // Fetch Calls (Conversations)
        const conversationData = await listConversations(ensuredId, { signal: controller.signal })

        // Fetch AI Chats
        const calls: DashboardItem[] = conversationData.map(c => ({ ...c, type: 'CALL' as const }))

        let chats: DashboardItem[] = []
        try {
          const chatData = await listChats(ensuredId, controller.signal)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
          chats = (chatData || []).map((c: any) => ({ ...c, type: 'CHAT' }))
        } catch {
          // non-fatal — proceed with empty chats
        }

        setItems([...calls, ...chats])
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load dashboard items', error)
          setItems([])
          setLoadError('Unable to load conversations.')
        }
      } finally {
        if (!controller.signal.aborted) setDataLoading(false)
      }
    }
    loadData()
    return () => controller.abort()
  }, [userId])

  const handleFilterChange = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
    setActiveFilter(value as any)
    setLoading(true)
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => setLoading(false), 550)
  }

  useEffect(
    () => () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    },
    [],
  )

  const handleSelectItem = useCallback(
    (id: string, type?: 'CALL' | 'CHAT') => {
      if (type === 'CHAT') {
        navigate(`/assistant?id=${id}`)
      } else {
        navigate(`/conversation/${id}`)
      }
    },
    [navigate],
  )

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handleArchiveSelected = useCallback(async () => {
    if (!selectedIds.size || archivingSelected) return
    const confirmed = window.confirm(`Archive ${selectedIds.size} selected item${selectedIds.size === 1 ? '' : 's'}?`)
    if (!confirmed) return
    try {
      setArchivingSelected(true)
      setArchiveError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      
      const itemsById = new Map(items.map((i) => [i.id, i]))
      const results = await Promise.all(
        Array.from(selectedIds).map(async (id) => {
          const item = itemsById.get(id)
          try {
            if (item?.type === 'CHAT') {
              await archiveChat(id, ensuredId)
            } else {
              await archiveConversation(id, ensuredId)
            }
            return { id, ok: true }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentional catch-all
          } catch (_error) {
            return { id, ok: false }
          }
        }),
      )
      const archivedIds = new Set(results.flatMap((r) => r.ok ? [r.id] : []))
      const failedIds = results.flatMap((r) => r.ok ? [] : [r.id])
      
      if (archivedIds.size) {
        setItems((prev) => prev.filter((item) => !archivedIds.has(item.id)))
        setSelectedIds((prev) => {
          const next = new Set(prev)
          archivedIds.forEach((id) => next.delete(id))
          return next
        })
      }
      if (failedIds.length) {
        setArchiveError(`Unable to archive ${failedIds.length} item${failedIds.length === 1 ? '' : 's'}.`)
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentional catch-all
    } catch (_error) {
      setArchiveError('Unable to archive selected items.')
    } finally {
      setArchivingSelected(false)
    }
  }, [archivingSelected, selectedIds, userId, items])

  return (
    <>
      <ContentHeader
        title="myScribbl Calls"
        filters={filters}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        actions={
          selectedIds.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className="danger-button header-action-button"
                onClick={handleArchiveSelected}
                disabled={archivingSelected}
              >
                {archivingSelected ? 'Archiving...' : `Archive selected (${selectedIds.size})`}
              </button>
              <button type="button" className="ghost-button header-action-button" onClick={handleClearSelection}>
                Cancel
              </button>
            </div>
          )
        }
      />
      <div className="content-panel">
        {archiveError && <div className="muted archive-error">{archiveError}</div>}
        {loading || dataLoading ? (
          <LoadingPlaceholder variant={placeholderVariant} />
        ) : loadError ? (
          <div data-testid="dashboard-error" className="muted archive-error">{loadError}</div>
        ) : activeFilter === 'Today' && filteredItems.length > 0 ? (
          <RecapDaySchedule
            items={filteredItems}
            onSelect={(id, type) => handleSelectItem(id, type)}
            className="recap-day-schedule-max"
          />
        ) : activeFilter === 'This Week' && filteredItems.length > 0 ? (
          <RecapWeekList 
            items={filteredItems} 
            onSelect={(id, type) => handleSelectItem(id, type)} 
          />
        ) : filteredItems.length > 0 ? (
          <RecapTileGrid
            items={filteredItems}
            onSelect={handleSelectItem}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
          />
        ) : filteredItems.length === 0 ? (
          <div data-testid="dashboard-empty">
            <EmptyState
              description='Call logs will appear once you add them. Check back soon to review and share updates.'
              tags={[]}
            />
          </div>
        ) : (
          <RecapPlaceholder key={activeFilter} variant={placeholderVariant} />
        )}
      </div>
    </>
  )
}

export default DashboardView
